import { Injectable } from "@angular/core";
import { UserManager, User, WebStorageStateStore, UserManagerSettings } from "oidc-client";
import { BehaviorSubject } from "rxjs";
import { LogService } from "./log.service";
import { AzureSearchService } from "./../../azure-search/azure-search";
import { EnvironmentConfig } from "../models/environment-config.model";
import { Account } from "../../account/account";
import { LocalStorageService, LocalStorageKeys } from "../services/local-storage.service";

declare var environment: EnvironmentConfig;

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private userManager: UserManager;
  private userManagerConfig: UserManagerSettings;

  private user: User;

  public initialized = new BehaviorSubject<boolean>(false);
  public onLoggedIn = new BehaviorSubject<any>(null);
  public accountInfo$ = new BehaviorSubject<Account>(null);

  constructor(private azureSearchService: AzureSearchService, private logService: LogService, private localStorageService: LocalStorageService) {
    this.userManagerConfig = {
      authority: `${environment.identityProvider}/`,
      client_id: environment.clientId,
      redirect_uri: environment.clientLoginRedirectUri,
      automaticSilentRenew: true,
      silent_redirect_uri: environment.clientSilentLoginRedirectUri,
      post_logout_redirect_uri: environment.clientLogoutRedirectUri,
      response_type: "id_token token",
      scope: environment.identityScope ? environment.identityScope : "openid catalog inventory account user estimate contracts profile inspections",
      filterProtocolClaims: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    };

    this.userManager = new UserManager(this.userManagerConfig);

    this.userManager.getUser().then((user) => {
      if (user && !user.expired) {
        this.user = user;
        this.getAccountInfo();
        this.logService.setUser(this.user.profile.sub, this.user.profile.account_id);
      } else {
        // try to sign in the user silently.
        this.userManager.signinSilent().then(
          (user) => {
            this.user = user;
            this.getAccountInfo();
            this.logService.setUser(this.user.profile.sub, this.user.profile.account_id);
          },
          (error) => {
            this.userManager.removeUser();
            this.userManager.clearStaleState();
            this.logService.clearUser();
          },
        );
      }
    });

    // we need to refresh the user in this service once the silent refresh callback occurs in silent-callback.html
    this.userManager.events.addUserLoaded((user) => (this.user = user));
    this.logAccountAccess();
  }

  logAccountAccess() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("login")) {
      this.logService.pushTagToGtm({
        event: "accountAccess",
        accessType: "login",
        client: environment.clientId,
      });
    }

    if (urlParams.get("signup")) {
      this.logService.pushTagToGtm({
        event: "accountAccess",
        accessType: "signUp",
        client: environment.clientId,
      });
    }
  }

  getAccountInfo() {
    // as of right now we only need marketplace to get the account info
    if (environment.clientId !== "rubbl.website") {
      return;
    }

    this.azureSearchService.getOne<any>(this.user.profile.account_id, "accounts").subscribe((account: Account) => {
      return this.accountInfo$.next(account);
    });
  }

  getUser(): Promise<User> {
    return this.userManager.getUser();
  }

  getUserNonAsync() {
    return this.user;
  }

  login(redirectPath: string = window.location.pathname + window.location.search): Promise<void> {
    if (environment.routePrefix) {
      redirectPath = `${environment.routePrefix}${redirectPath}`; // the redirect path comes with a trailing slash
    }

    redirectPath += redirectPath.includes("?") ? "&login=true" : "?login=true"; // need this to fire a login succesful event. really do not like this solution but will get reworked when we have new identity provider

    return this.userManager.signinRedirect({ state: { redirectPath } });
  }

  logout(): Promise<void> {
    this.userManager.clearStaleState();
    this.logService.clearUser();

    // remove some local storage keys
    switch (environment.clientId) {
      case "rubbl.inspections":
        this.localStorageService.set(LocalStorageKeys.InspectionWelcome, null);
        break;
    }

    return this.userManager.signoutRedirect();
  }

  isLoggedIn(): boolean {
    return this.user != null && !this.user.expired;
  }

  getAccessToken(): string {
    return this.user ? this.user.access_token : "";
  }

  getRoles(): string[] {
    if (!this.user) {
      return null;
    }

    return Array.isArray(this.user.profile.role) ? this.user.profile.role : [this.user.profile.role];
  }

  signInSilent() {
    return this.userManager.signinSilent();
  }

  signoutRedirectCallback(): Promise<any> {
    return this.userManager.signoutRedirectCallback();
  }

  signUp(redirectPath: string = window.location.pathname + window.location.search): Promise<any> {
    redirectPath += redirectPath.includes("?") ? "&signup=true" : "?signup=true"; // need this to fire a signup succesful event
    return this.userManager.createSigninRequest({ state: { redirectPath } }).then((req) => {
      const keepAuth = this.userManagerConfig.authority.replace("/auth/", "");
      const localUrl = req.url.replace(keepAuth, "");
      window.location.href = `${this.userManagerConfig.authority}Account/SignUp?returnUrl=${encodeURIComponent(localUrl)}`;
    });
  }

  setUser(user) {
    this.user = user;
    this.onLoggedIn.next(user);
  }
}
